import "./styles.css";
import "./styles.css";

import React, { useRef, useState, useEffect, useContext } from "react";
import Select from "react-select";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";

import { useWhatsappContext } from "../../../contexts/Whatsapp";
import { useInactivity } from "../../../hooks";
import MsgBox from "./components/MsgBox";
import Offcanvas from "./components/Offcanvas";
import PanelChat from "./components/PanelChat";
import WhatsAppBusinessIcon from "./components/Icons/wabusiness";
import WhatsAppIcon from "./components/Icons/whatsapp";
import WhatsAppVerifiedIcon from "./components/Icons/whatsapp-verified";
import WhatsAppUserIcon from "./components/Icons/whatsapp-user";
import Language from "./components/Icons/language";
import Instagram from "./components/instagram";
import api from "../../../services/api";
import AuthContext from "../../../contexts/Auth";
import EmpresaContext from "../../../contexts/Empresa";

interface Inbox {
  id: number;
  account_id: number;
  name: string;
  channel_type: string;
}

interface Account {
  account_id: number;
  name: string;
}

interface ToolbarInfo {
  chat_empresa: number;
  conversas_email: string | null;
  conversas_password: string | null;
  chat_inboxes: Inbox[];
  chat_accounts: Account[];
  chat_availability: number | null;
}

// Status do usuário no Chatwoot (coluna account_users.availability).
const AVAILABILITY_OPTIONS: { value: number; label: string; color: string }[] = [
  { value: 0, label: "Online", color: "#2dce89" },
  { value: 1, label: "Ausente", color: "#ffb236" },
  { value: 2, label: "Offline", color: "#adb5bd" },
];

export default function Chat() {
  const { user } = useContext(AuthContext);
  const { empresaSelecionada } = useContext(EmpresaContext);

  const [url, setUrl] = useState(process.env.REACT_APP_PLAYNEE_CONVERSAS_URL);
  const [selectedInbox, setSelectedInbox] = useState<{
    id: number;
    account_id: number;
  } | null>(null);
  const [toolbarInfo, setToolbarInfo] = useState<ToolbarInfo | null>(null);
  const [availability, setAvailability] = useState<number | null>(null);

  const getTollbarInfo = async () => {
    const response = await api.post(`chat/toolbar-info`, {
      user_id: user?.id,
      empresa_id: empresaSelecionada?.id,
    });

    const { conversas_email, conversas_password } = response.data;

    if (conversas_email && conversas_password) {
      setUrl(
        `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/auth/sign_in/load?email=${encodeURIComponent(
          conversas_email,
        )}&password=${encodeURIComponent(conversas_password)}`,
      );
    } else {
      setUrl(
        `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${response.data.chat_empresa}/dashboard`,
      );
    }
    setToolbarInfo(response.data);
    setAvailability(response.data.chat_availability ?? null);
  };

  const handleChangeAvailability = async (value: number) => {
    const previous = availability;
    setAvailability(value); // atualização otimista
    try {
      await api.post(`chat/availability`, {
        user_id: user?.id,
        availability: value,
      });
    } catch (err) {
      setAvailability(previous); // reverte em caso de falha
    }
  };

  useEffect(() => {
    getTollbarInfo();
  }, [empresaSelecionada]);

  useEffect(() => {
    if (selectedInbox) {
      setUrl(
        `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${selectedInbox.account_id}/inbox/${selectedInbox.id}`,
      );
    }
  }, [selectedInbox]);

  const customStyles = {
    container: (provided: any) => ({ ...provided, flex: 1 }),
    option: (provided: any) => ({ ...provided, zIndex: 100, fontSize: "13px" }),
    menu: (provided: any) => ({ ...provided, zIndex: 100 }),
    control: (provided: any) => ({
      ...provided,
      minHeight: "43px",
      fontSize: "13px",
    }),
  };

  const currentStatus =
    AVAILABILITY_OPTIONS.find((o) => o.value === availability) ?? {
      label: "Status",
      color: "#adb5bd",
    };

  return (
    <>
      <div
        className="w-100"
        style={{ height: "100vh", display: "flex", flexDirection: "column" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: ".25rem",
            border: "1px solid #e9ecef",
            background: "#fff",
            padding: ".75rem",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {toolbarInfo && (
            <>
              <Button
                id="tooltip-inbox"
                color="primary"
                onClick={() => {
                  if (toolbarInfo?.chat_empresa) {
                    setUrl(
                      `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${toolbarInfo.chat_empresa}/dashboard`,
                    );
                  }
                }}
              >
                <i className="fas fa-inbox" style={{ color: "#fff" }}></i>
              </Button>
              <UncontrolledTooltip placement="top" target="tooltip-inbox">
                Caixa de Entrada
              </UncontrolledTooltip>

              <Button
                id="tooltip-search"
                color="primary"
                onClick={() => {
                  if (toolbarInfo?.chat_empresa) {
                    setUrl(
                      `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${toolbarInfo.chat_empresa}/search`,
                    );
                  }
                }}
              >
                <i className="fas fa-search" style={{ color: "#fff" }}></i>
              </Button>
              <UncontrolledTooltip placement="top" target="tooltip-search">
                Buscar
              </UncontrolledTooltip>

              <Button
                id="tooltip-participants"
                color="primary"
                onClick={() => {
                  if (toolbarInfo?.chat_empresa) {
                    setUrl(
                      `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${toolbarInfo.chat_empresa}/participating/conversations`,
                    );
                  }
                }}
              >
                <i className="fas fa-users" style={{ color: "#fff" }}></i>
              </Button>
              <UncontrolledTooltip
                placement="top"
                target="tooltip-participants"
              >
                Participantes
              </UncontrolledTooltip>

              <div style={{ width: "300px" }}>
                <Select
                  placeholder="Selecione a Conta"
                  styles={customStyles}
                  onChange={(item: any) => {
                    if (!item) return;
                    if (item.type === "dashboard") {
                      setSelectedInbox(null);
                      setUrl(
                        `${process.env.REACT_APP_PLAYNEE_CONVERSAS_URL}/app/accounts/${item.account_id}/dashboard`,
                      );
                    } else {
                      setSelectedInbox({
                        id: item.value,
                        account_id: item.account_id,
                      });
                    }
                  }}
                  options={[
                    ...(toolbarInfo?.chat_accounts?.length
                      ? [
                          {
                            label: "Todas as conversas",
                            options: toolbarInfo.chat_accounts.map(
                              (account: any) => ({
                                value: `dashboard-${account.account_id}`,
                                account_id: account.account_id,
                                label: account.name,
                                type: "dashboard",
                              }),
                            ),
                          },
                        ]
                      : []),
                    {
                      label: "Caixas de entrada",
                      options: (toolbarInfo?.chat_inboxes ?? []).map(
                        (inbox: any) => ({
                          value: inbox.id,
                          account_id: inbox.account_id,
                          label: inbox.name,
                          channel_type: inbox.channel_type,
                          type: "inbox",
                        }),
                      ),
                    },
                  ]}
                  formatOptionLabel={(option: any) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {option.type === "dashboard" ? (
                        <i className="fas fa-th-large" style={{ fontSize: 13 }} />
                      ) : option.channel_type === "api" ||
                        option.channel_type === "whatsapp" ? (
                        <WhatsAppIcon />
                      ) : option.channel_type === "webwidget" ? (
                        <Language />
                      ) : (
                        <Instagram />
                      )}

                      {option.label}
                    </div>
                  )}
                />
              </div>

              <UncontrolledDropdown className="chat-status-dropdown">
                <DropdownToggle
                  caret
                  color="secondary"
                  outline
                  className="chat-status-toggle"
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: currentStatus.color,
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  {currentStatus.label}
                </DropdownToggle>
                <DropdownMenu right className="chat-status-menu">
                  {AVAILABILITY_OPTIONS.map((option) => (
                    <DropdownItem
                      key={option.value}
                      active={availability === option.value}
                      onClick={() => handleChangeAvailability(option.value)}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: option.color,
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </>
          )}
        </div>
        <iframe
          src={url}
          style={{
            width: "100%",
            flex: 1,
            border: 0,
            display: "block",
            minHeight: 0,
          }}
          title="playnee-conversas"
        />
      </div>
    </>
  );
}
